'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import {
  trackFeatureUsed,
  trackSafePageView,
  trackWalletAction,
} from '@workspace/analytics/posthog/events';
import { useTrackOnce } from '@workspace/analytics/posthog/use-track-once';
import { Button } from '@workspace/ui/components/button';
import { Card as UICard } from '@workspace/ui/components/custom/card';
import { PlusIcon } from '@workspace/ui/components/custom-icons/PlusIcon';
import { SendIcon } from '@workspace/ui/components/custom-icons/SendIcon';
import { Skeleton } from '@workspace/ui/components/skeleton';
import { InfoTooltip } from '@workspace/ui/components/custom/info-tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip';
import { EPS_CARD_REQUEST_STATUS_OPTIONS, formatAmount, PERMISSIONS } from '@workspace/ui/lib';
import type { Currency } from '@workspace/ui/types';
import { ArrowLeftRight, Coins, InfoIcon } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FeatureLockedDialog } from '@/components/feature-lock/feature-locked-dialog';
import { RestrictionDialog } from '@/components/restrictions/restriction-dialog';
import { BankDetailsDialog } from '@/components/wallet/bank-details-dialog';
import { CardActivationSuccessDialog } from '@/components/wallet/card-activation-success-dialog';
import { CurrencySwitcher } from '@/components/wallet/currency-switcher';
import {
  CardRequestStatus,
  type CardRequestStatus as CardRequestStatusType,
  type CardStatusData,
} from '@/components/wallet/card-request-status';
import { CurrencyConversionDialog } from '@/components/wallet/currency-conversion-dialog';
// import MobileAppPromoBanner from '@/components/wallet/mobile-app-promo-banner';
import { QueuedPaymentsSection } from '@/components/wallet/queued-payments-section';
import { SalaryWalletActiveDialog } from '@/components/wallet/salary-wallet-active-dialog';
import { TransactionDetailsDialog } from '@/components/wallet/transaction-details-dialog';
import { type Transaction, TransactionsTable } from '@/components/wallet/transactions-table';
import { WalletCard } from '@/components/wallet/wallet-card';
import { useAuth } from '@/hooks/use-auth';
import { useSystemBankDetails } from '@/hooks/use-banks';
import { type NativeCard, useCardRequests, useNativeCards } from '@/hooks/use-cards';
import { useCountries } from '@/hooks/use-countries';
import { useExpenseCategories } from '@/hooks/use-expenses';
import { useFeatureLockGate } from '@/hooks/use-feature-lock';
import {
  hasEpsCardPermission,
  useCurrentKYCVerification,
  useKYCVerifications,
} from '@/hooks/use-kyc';
import { useUserDetails, useUserVerification } from '@/hooks/use-user';
import { useUserSettings } from '@/hooks/use-settings';
import { useRestrictionGate, useUserRestriction } from '@/hooks/use-user-restriction';
import {
  useCreateEurWallet,
  useRequestWallet,
  useSetWalletVisibility,
  useWalletBalance,
  useWallets,
} from '@/hooks/use-wallet';
import { BANK_DETAILS_CURRENCIES } from '@/lib/constants';
import { isUserFromForbiddenCountryById } from '@/lib/country-restrictions';
import { parseQACardEpsStatus, parseQACardStatus } from '@/lib/qa-utils';
import type { SystemBankDetails } from '@/types/bank';
import { USER_RESTRICTION_TYPES } from '@/types/restriction';

interface WalletPageClientProps {
  currency: Currency;
  allCurrencies: Currency[];
  canUseBatchTransfers: boolean;
  hasSalaryWalletAccess: boolean;
  showMobileAppPromoBanner: boolean;
}

// Card request statuses before the card is sent. Admin moves a request through these
// (new, accepting, accepted, or failed and resent to EPS) until it is marked as sent.
const PREPARING_CARD_REQUEST_STATUSES: string[] = [
  EPS_CARD_REQUEST_STATUS_OPTIONS.NEW,
  EPS_CARD_REQUEST_STATUS_OPTIONS.PENDING,
  EPS_CARD_REQUEST_STATUS_OPTIONS.ACCEPTING,
  EPS_CARD_REQUEST_STATUS_OPTIONS.ACCEPTED,
  EPS_CARD_REQUEST_STATUS_OPTIONS.FAILED,
];

export default function WalletPageClient({
  currency,
  allCurrencies,
  canUseBatchTransfers,
  hasSalaryWalletAccess,
  // showMobileAppPromoBanner,
}: WalletPageClientProps) {
  const router = useRouter();
  const [isRequestingWallet, setIsRequestingWallet] = useState(false);
  const [hasAttemptedEurWalletCreation, setHasAttemptedEurWalletCreation] = useState(false);

  const isBatchTransfersEnabled = canUseBatchTransfers;
  const { isAuthenticated, accessToken, isLoading: authLoading, user } = useAuth();
  const { guard, guardNavigation, restrictionDialogProps } = useRestrictionGate();
  const {
    guardNavigation: guardSendMoneyNavigation,
    featureLockedDialogProps: sendMoneyFeatureLockedDialogProps,
  } = useFeatureLockGate(PERMISSIONS.CREATE_SELF_TRANSACTIONS);

  const authOrganisationId = user?.active_organisation?.id ?? user?.organisations?.[0]?.id;

  // Get user verification data early (needed for wallet filtering)
  const { data: userVerification } = useUserVerification(
    isAuthenticated && !!accessToken,
    authOrganisationId
  );

  const { data: userSettings } = useUserSettings(
    userVerification?.id ?? 0,
    authOrganisationId ?? 0,
    isAuthenticated && !!accessToken
  );

  // Fetch all wallets to check which ones exist (filtered by authenticated user's ID)
  const { data: userWallets, isLoading: walletsLoading } = useWallets(
    isAuthenticated && !!accessToken && !!userVerification?.id,
    {
      userId: userVerification?.id,
    }
  );

  // Request wallet mutation
  const requestWalletMutation = useRequestWallet();

  const setWalletVisibilityMutation = useSetWalletVisibility();

  // Create EUR wallet mutation
  const createEurWalletMutation = useCreateEurWallet();

  // Automatically create EUR wallet if user has no wallets
  useEffect(() => {
    if (
      !walletsLoading &&
      userWallets !== undefined &&
      userWallets.length === 0 &&
      !hasAttemptedEurWalletCreation &&
      isAuthenticated &&
      accessToken
    ) {
      setHasAttemptedEurWalletCreation(true);
      setIsRequestingWallet(true);

      toast.info('Setting up your EUR wallet...');

      createEurWalletMutation.mutate(undefined, {
        onSuccess: () => {
          toast.dismiss();
          toast.success('Your EUR wallet has been created successfully.');
          setIsRequestingWallet(false);
        },
        onError: (error) => {
          console.error('Failed to create EUR wallet:', error);
          toast.dismiss();
          const errorMessage =
            error instanceof Error ? error.message : 'Please try again or contact support.';
          toast.error(`Failed to create EUR wallet: ${errorMessage}`);
          setIsRequestingWallet(false);
        },
      });
    }
  }, [
    walletsLoading,
    userWallets,
    hasAttemptedEurWalletCreation,
    isAuthenticated,
    accessToken,
    createEurWalletMutation,
  ]);

  // Handle currency change with wallet request
  const handleCurrencyChange = async (newCurrencyCode: string) => {
    // Bail out if wallets are still loading or undefined
    if (walletsLoading || userWallets === undefined) {
      return;
    }

    const targetCurrency = allCurrencies.find((c) => c.code === newCurrencyCode);
    if (!targetCurrency) return;

    // Check if wallet already exists for this currency
    const existingWallets = userWallets.filter((w) => w.currency_id === targetCurrency.id);
    const hiddenWallet = existingWallets.find((w) => w.type === 'default') ?? existingWallets[0];

    if (hiddenWallet && existingWallets.every((w) => w.is_visible === false)) {
      setIsRequestingWallet(true);
      try {
        await setWalletVisibilityMutation.mutateAsync({
          walletId: hiddenWallet.id,
          isVisible: true,
        });
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Please try again or contact support.'
        );
        return;
      } finally {
        setIsRequestingWallet(false);
      }
    }

    if (existingWallets.length === 0) {
      // Need to request wallet first
      setIsRequestingWallet(true);
      toast.info(`Creating your ${targetCurrency.name} wallet...`);
      try {
        await requestWalletMutation.mutateAsync({
          currency_id: targetCurrency.id,
        });

        toast.dismiss();
        toast.success(`Your ${targetCurrency.name} wallet has been created successfully.`);

        // Wait a bit for the backend to process
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Failed to request wallet:', error);
        toast.dismiss();
        const errorMessage =
          error instanceof Error ? error.message : 'Please try again or contact support.';
        toast.error(`Failed to create wallet: ${errorMessage}`);
        setIsRequestingWallet(false);
        return; // Don't navigate if wallet creation failed
      } finally {
        setIsRequestingWallet(false);
      }
    }

    router.push(`/${targetCurrency.code.toLowerCase()}`);
  };

  // Get URL search params for QA testing
  const searchParams = useSearchParams();

  // QA Testing: Override card status from URL parameters
  const qaCardStatus = searchParams.get('card-status');
  const qaCardEpsStatus = searchParams.get('card-eps-status');
  const qaShowBanner =
    searchParams.get('qa-mode') === 'true' || !!qaCardStatus || !!qaCardEpsStatus;

  // Get the active organization ID from user verification
  const organizationId =
    userVerification?.active_organisation?.id ?? userVerification?.organisations?.[0]?.id;

  // Prefetch user details to ensure fresh data is cached
  // The data is not used directly here but hydrates the cache for other components
  useUserDetails(
    userVerification?.id || 0,
    organizationId || 0,
    isAuthenticated && !!accessToken && !!userVerification?.id && !!organizationId
  );
  // Get countries data for restriction checking
  const { data: countries, isLoading: countriesLoading } = useCountries(true);

  const { data: expenseCategories = [] } = useExpenseCategories();

  // Get user's country_id from user verification data
  const userCountryId = userVerification?.country_id;

  // Check if user is from a forbidden country
  const isFromForbiddenCountry = useMemo(() => {
    const isForbidden = isUserFromForbiddenCountryById(userCountryId, countries);

    return isForbidden;
  }, [userCountryId, countries]);

  // State for transaction details dialog
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // State for currency conversion dialog
  const [isCurrencyConversionDialogOpen, setIsCurrencyConversionDialogOpen] = useState(false);

  // State for card activation success dialog. Owned here (not inside CardRequestStatus)
  // so the dialog survives when the banner is swapped for the active WalletCard after activation.
  const [showCardActivationSuccess, setShowCardActivationSuccess] = useState(false);

  // State for queued payments (currency codes that have insufficient funds)
  const [queuedCurrencyCodes, setQueuedCurrencyCodes] = useState<string[]>([]);
  const hasQueuedPayments = queuedCurrencyCodes.includes(currency.code);

  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedTransaction(null);
  };

  // Fetch wallet balance for the selected currency - this includes bank_details
  const walletsEnabled = !!accessToken;
  const {
    data: walletBalance,
    isLoading: walletBalanceLoading,
    error: walletBalanceError,
  } = useWalletBalance(currency.id, walletsEnabled);

  useTrackOnce(() => {
    trackSafePageView('wallet-app', 'wallet_home');
    trackFeatureUsed('wallet-app', 'wallet');
  });
  // Fetch native cards for EPS service
  const cardsEnabled = !!accessToken;
  const { data: nativeCards, isLoading: nativeCardsLoading } = useNativeCards(
    'eps',
    {},
    cardsEnabled
  );

  // Fetch card requests for EPS service
  const { data: cardRequests, isLoading: cardRequestsLoading } = useCardRequests(
    'eps',
    cardsEnabled
  );

  // Fetch current KYC verification (drives banner status via status field)
  const { data: currentKyc, isLoading: currentKycLoading } =
    useCurrentKYCVerification(cardsEnabled);
  const { data: kycVerifications, isLoading: kycVerificationsLoading } =
    useKYCVerifications(cardsEnabled);
  const { data: userRestriction, isLoading: userRestrictionLoading } =
    useUserRestriction(cardsEnabled);

  // Card banner status is derived from the card, request, KYC, and restriction queries.
  // Until they resolve, determineCardStatus falls back to the "Start verification" banner,
  // which flashes before the real card view on a hard reload. The restriction query has to
  // finish too, or a finally restricted user briefly sees Order card or Resubmit. Keep the
  // rest of the wallet mounted and only skeleton the card slot — gating the whole page on
  // these flags previously caused a query loop.
  const isCardStatusLoading =
    cardsEnabled &&
    !qaCardStatus &&
    !qaCardEpsStatus &&
    (nativeCardsLoading ||
      cardRequestsLoading ||
      currentKycLoading ||
      kycVerificationsLoading ||
      userRestrictionLoading);

  // Helper function to determine status based on KYC and card request data
  const determineCardStatus = (): {
    displayCard: NativeCard | null;
    cardRequestStatus: CardRequestStatusType;
    cardData: CardStatusData | undefined;
  } => {
    // QA Testing: Override with card-eps-status URL parameter if provided (simulates card with specific EPS status)
    const qaOverrideEpsStatus = parseQACardEpsStatus(qaCardEpsStatus);
    if (qaCardEpsStatus !== null && qaOverrideEpsStatus) {
      // Use existing card as base if available, otherwise create mock card for QA testing
      const baseCard = nativeCards?.[0];
      const cardWithOverriddenStatus: NativeCard = baseCard
        ? { ...baseCard, eps_status: qaOverrideEpsStatus }
        : {
            id: 999,
            card_request_id: 999,
            user_id: 0,
            currency: 'EUR',
            expiration: '12/30',
            exp_year: '2030',
            exp_month: '12',
            last4: '0000',
            holder: 'QA Test',
            eps_card_id: 'qa-test-card-eps-123',
            eps_account_id: '',
            eps_tenant_id: '',
            eps_status: qaOverrideEpsStatus,
            eps_status_id: 0,
            eps_alias: '',
            is_virtual: false,
            activation_request_sent: true,
            created_at: null,
            updated_at: null,
          };

      return {
        displayCard: cardWithOverriddenStatus,
        cardRequestStatus: null,
        cardData: {
          id: cardWithOverriddenStatus.id,
          eps_card_id: cardWithOverriddenStatus.eps_card_id,
          service: 'eps',
        },
      };
    }

    // QA Testing: Override with card-status URL parameter if provided (simulates card request status)
    const qaOverrideStatus = parseQACardStatus(qaCardStatus);
    if (qaOverrideStatus !== false) {
      // Create mock card data for QA testing
      const mockCardData: CardStatusData = {
        id: 999,
        eps_card_id: 'qa-test-card-123',
        service: 'eps',
        tracking_number:
          qaOverrideStatus === 'on-way' || qaOverrideStatus === 'preparing-delivery'
            ? 'DHL Express • RN1234 4456 3GB'
            : null,
        tracking_link:
          qaOverrideStatus === 'on-way' || qaOverrideStatus === 'preparing-delivery'
            ? 'https://www.dhl.com/'
            : null,
      };

      return {
        displayCard: null,
        cardRequestStatus: qaOverrideStatus,
        cardData: qaOverrideStatus ? mockCardData : undefined,
      };
    }

    // Get the first active native card
    const activeBlockedOrFrozenCard =
      nativeCards?.find(
        (card) =>
          card.eps_status === 'Active' ||
          card.eps_status === 'BlockedByCustomer' ||
          card.eps_status === 'BlockedByIssuer' ||
          card.eps_status === 'BlockedByPartner'
      ) || null;

    // Get the first inactive/closing card
    const inactiveCard =
      nativeCards?.find(
        (card) => card.eps_status === 'InActive' || card.eps_status === 'Closing'
      ) || null;

    // Get the latest card request
    const latestCardRequest =
      cardRequests && cardRequests.length > 0 ? cardRequests.sort((a, b) => b.id - a.id)[0] : null;

    const trackingData = {
      tracking_number: latestCardRequest?.tracking_number ?? null,
      tracking_link: latestCardRequest?.tracking_link ?? null,
    };

    if (activeBlockedOrFrozenCard) {
      return {
        displayCard: activeBlockedOrFrozenCard,
        cardRequestStatus: null,
        cardData: {
          id: activeBlockedOrFrozenCard.id,
          eps_card_id: activeBlockedOrFrozenCard.eps_card_id,
          service: 'eps',
        },
      };
    }

    // If we have inactive cards but no active ones
    if (inactiveCard) {
      // If the card hasn't been activated yet (activation_request_sent is false),
      // the card exists and is on its way to the user (ready to be activated)
      if (!inactiveCard.activation_request_sent) {
        // Show 'on-way' only if card request status is 'sent', otherwise show 'preparing-delivery'
        const cardRequestIsSent = latestCardRequest?.status === 'sent';
        return {
          displayCard: null,
          cardRequestStatus: cardRequestIsSent ? 'on-way' : 'preparing-delivery',
          cardData: {
            id: inactiveCard.id,
            eps_card_id: inactiveCard.eps_card_id,
            service: 'eps',
            ...trackingData,
          },
        };
      }

      // Otherwise, show inactive status (card was activated but is now deactivated)
      return {
        displayCard: null,
        cardRequestStatus: 'inactive',
        cardData: {
          id: inactiveCard.id,
          eps_card_id: inactiveCard.eps_card_id,
          service: 'eps',
        },
      };
    }

    // Starting card verification creates a new Sumsub KYC, while an older approved KYC
    // (for example a demo one) can stay current. Until the card permission is granted,
    // the latest Sumsub KYC is the one that describes the card verification.
    const latestKyc = kycVerifications?.[0];
    const kycStatus =
      !hasEpsCardPermission(currentKyc) && latestKyc?.type === 'sumsub'
        ? latestKyc.status
        : currentKyc?.status;
    // Admin AML "final rejected" is stored as a restriction, not as the KYC status.
    const isFinalRestriction =
      userRestriction?.type === USER_RESTRICTION_TYPES.FINAL ||
      userRestriction?.type === USER_RESTRICTION_TYPES.BLOCK;

    if (kycStatus === 'final_rejected' || isFinalRestriction) {
      return {
        displayCard: null,
        cardRequestStatus: 'final-rejected',
        cardData: undefined,
      };
    }

    if (kycStatus === 'pending') {
      return {
        displayCard: null,
        cardRequestStatus: 'verification-progress',
        cardData: undefined,
      };
    }

    if (kycStatus === 'rejected') {
      return {
        displayCard: null,
        cardRequestStatus: 'rejected',
        cardData: undefined,
      };
    }

    if (kycStatus === 'approved') {
      if (latestCardRequest) {
        const cardStatus = latestCardRequest.status?.toLowerCase().trim();
        const requestCardData = {
          id: latestCardRequest.id,
          eps_card_id: '',
          service: 'eps',
          ...trackingData,
        };

        if (cardStatus === EPS_CARD_REQUEST_STATUS_OPTIONS.DECLINED) {
          return {
            displayCard: null,
            cardRequestStatus: 'card-declined',
            cardData: requestCardData,
          };
        }

        if (cardStatus === EPS_CARD_REQUEST_STATUS_OPTIONS.SENT) {
          return {
            displayCard: null,
            cardRequestStatus: 'on-way',
            cardData: requestCardData,
          };
        }

        if (cardStatus && PREPARING_CARD_REQUEST_STATUSES.includes(cardStatus)) {
          return {
            displayCard: null,
            cardRequestStatus: 'preparing-delivery',
            cardData: requestCardData,
          };
        }
      }

      return {
        displayCard: null,
        cardRequestStatus: hasEpsCardPermission(currentKyc)
          ? 'ready-to-order'
          : 'start-verification',
        cardData: undefined,
      };
    }

    // A Sumsub applicant was created but never submitted
    if (kycStatus === 'initial') {
      return {
        displayCard: null,
        cardRequestStatus: 'continue-verification',
        cardData: undefined,
      };
    }

    return {
      displayCard: null,
      cardRequestStatus: 'start-verification',
      cardData: undefined,
    };
  };

  const shouldShowBankDetails = BANK_DETAILS_CURRENCIES.includes(currency.code);

  // Only show card components for EUR, USD, and GBP
  const CARD_SUPPORTED_CURRENCIES = ['EUR'];
  const shouldShowCardComponents = CARD_SUPPORTED_CURRENCIES.includes(currency.code);

  // Get system bank details for the selected currency
  // Note: This hook needs to be called before any early returns to ensure it's executed on init
  // Enable the query only when authenticated, have access token, and currency supports bank details
  const {
    data: systemBankDetails,
    isLoading: systemBankDetailsLoading,
    error: systemBankDetailsError,
  } = useSystemBankDetails(
    currency.code,
    undefined,
    isAuthenticated && !!accessToken && shouldShowBankDetails
  );

  // Determine card status and display logic
  const { displayCard, cardRequestStatus, cardData } = useMemo(determineCardStatus, [
    nativeCards,
    cardRequests,
    currentKyc,
    kycVerifications,
    userRestriction,
    qaCardStatus,
    qaCardEpsStatus,
  ]);

  const activatedCurrencies = useMemo(() => {
    if (!userWallets) return [];

    const currenciesById = new Map(allCurrencies.map((curr) => [curr.id, curr]));
    const seenCurrencyIds = new Set<number>();
    const visibleCurrencies: Currency[] = [];
    // The default wallet decides the currency's position, so a sorted list isn't
    // reordered by a pre_payment wallet of the same currency.
    const defaultWalletCurrencyIds = new Set(
      userWallets
        .filter((wallet) => wallet.type === 'default' && wallet.is_visible !== false)
        .map((wallet) => wallet.currency_id)
    );

    for (const wallet of userWallets) {
      if (wallet.is_visible === false || seenCurrencyIds.has(wallet.currency_id)) continue;
      if (wallet.type !== 'default' && defaultWalletCurrencyIds.has(wallet.currency_id)) continue;

      const curr = currenciesById.get(wallet.currency_id);
      if (!curr) continue;

      seenCurrencyIds.add(wallet.currency_id);
      visibleCurrencies.push(curr);
    }

    return visibleCurrencies;
  }, [userWallets, allCurrencies]);

  const availableCurrencies = useMemo(() => {
    if (!userWallets) return [];

    const walletCurrencyIds = new Set(userWallets.map((wallet) => wallet.currency_id));

    return allCurrencies.filter((curr) => !walletCurrencyIds.has(curr.id));
  }, [userWallets, allCurrencies]);

  const hiddenCurrencies = useMemo(() => {
    const activatedCurrencyIds = new Set(activatedCurrencies.map((curr) => curr.id));
    const walletCurrencyIds = new Set(userWallets?.map((wallet) => wallet.currency_id));

    return allCurrencies.filter(
      (curr) => walletCurrencyIds.has(curr.id) && !activatedCurrencyIds.has(curr.id)
    );
  }, [userWallets, allCurrencies, activatedCurrencies]);

  const currencyCodeUrlParam = useMemo(
    () => new URLSearchParams({ currency: currency.code }).toString(),
    [currency.code]
  );

  const canTrackWalletRewardsCard =
    !authLoading &&
    isAuthenticated &&
    !countriesLoading &&
    !walletBalanceLoading &&
    !!walletBalance?.balance_rewards_enabled;

  useTrackOnce(() => {
    trackWalletAction('balance_rewards_viewed', {
      flow: 'balance_rewards',
      surface: 'wallet_card',
    });
  }, canTrackWalletRewardsCard);

  // If still loading auth state or not authenticated, show skeleton loader
  if (authLoading || (!authLoading && !isAuthenticated) || countriesLoading) {
    return (
      <div className="min-h-screen">
        <div className="mx-auto max-w-dvw">
          {/* Main content grid skeleton */}
          <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
            {/* Balance Card Skeleton */}
            <div className="w-full">
              <UICard className="h-[480px]">
                <div className="mb-7 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="bg-grey-200 dark:bg-grey-800 h-8 w-8 rounded-full" />
                    <Skeleton className="bg-grey-200 dark:bg-grey-800 h-6 w-16" />
                  </div>
                  <Skeleton className="bg-grey-200 dark:bg-grey-800 h-8 w-24" />
                </div>

                <div className="space-y-5">
                  <div className="flex flex-col">
                    <Skeleton className="bg-grey-200 dark:bg-grey-800 mb-1 h-4 w-32" />
                    <Skeleton className="bg-grey-200 dark:bg-grey-800 h-12 w-48" />
                  </div>

                  <div className="mb-10 space-y-5">
                    <div className="flex flex-col">
                      <Skeleton className="bg-grey-200 dark:bg-grey-800 mb-1 h-4 w-28" />
                      <Skeleton className="bg-grey-200 dark:bg-grey-800 h-5 w-24" />
                    </div>
                    <div className="flex flex-col">
                      <Skeleton className="bg-grey-200 dark:bg-grey-800 mb-1 h-4 w-32" />
                      <Skeleton className="bg-grey-200 dark:bg-grey-800 h-5 w-24" />
                    </div>
                  </div>
                </div>

                {/* Action Buttons Skeleton */}
                <div className="flex flex-wrap gap-4 sm:gap-8">
                  <div className="flex flex-col items-center">
                    <Skeleton className="bg-grey-200 dark:bg-grey-800 h-12 w-12 rounded-full" />
                    <Skeleton className="bg-grey-200 dark:bg-grey-800 mt-2 h-4 w-8" />
                  </div>
                  <div className="flex flex-col items-center">
                    <Skeleton className="bg-grey-200 dark:bg-grey-800 h-12 w-12 rounded-full" />
                    <Skeleton className="bg-grey-200 dark:bg-grey-800 mt-2 h-4 w-8" />
                  </div>
                </div>
              </UICard>
            </div>

            {/* Card Display Skeleton */}
            <CardDisplaySkeleton />
          </div>

          {/* Transactions Section Skeleton */}
          <div className="space-y-4">
            <Skeleton className="bg-grey-200 dark:bg-grey-800 h-8 w-48" />
            <UICard>
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="bg-grey-200 dark:bg-grey-800 h-10 w-10 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="bg-grey-200 dark:bg-grey-800 h-4 w-32" />
                        <Skeleton className="bg-grey-200 dark:bg-grey-800 h-3 w-24" />
                      </div>
                    </div>
                    <div className="text-right">
                      <Skeleton className="bg-grey-200 dark:bg-grey-800 h-4 w-20" />
                      <Skeleton className="bg-grey-200 dark:bg-grey-800 h-3 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            </UICard>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-dvw">
        {/* QA Testing Banner */}
        {qaShowBanner && (
          <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <InfoIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  QA Testing Mode Active
                </h3>
                <div className="mt-1 text-sm text-orange-700 dark:text-orange-300">
                  {qaCardEpsStatus && (
                    <>
                      <p>
                        Card EPS status is being simulated:{' '}
                        <code className="rounded bg-orange-100 px-1 font-mono dark:bg-orange-800">
                          card-eps-status={qaCardEpsStatus}
                        </code>
                      </p>
                      <p className="mt-1">
                        Available EPS statuses: active, blockedByCustomer, blockedByIssuer,
                        blockedByPartner
                      </p>
                      <p className="mt-1">
                        Example:{' '}
                        <code className="rounded bg-orange-100 px-1 font-mono dark:bg-orange-800">
                          ?qa-mode=true&card-eps-status=blockedByIssuer
                        </code>
                      </p>
                    </>
                  )}
                  {!qaCardEpsStatus && (
                    <>
                      <p>
                        Card request status is being overridden by URL parameter:{' '}
                        <code className="rounded bg-orange-100 px-1 font-mono dark:bg-orange-800">
                          card-status={qaCardStatus || 'none'}
                        </code>
                      </p>
                      <p className="mt-1">
                        Available statuses: none, start-verification, verification-required,
                        continue-verification, ready-to-order, verification-progress, rejected,
                        final-rejected, card-declined, preparing-delivery, on-way, inactive, active
                      </p>
                      <p className="mt-1">
                        Example:{' '}
                        <code className="rounded bg-orange-100 px-1 font-mono dark:bg-orange-800">
                          ?card-status=inactive
                        </code>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main content grid */}
        <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          {/* Balance Card */}
          <div className="flex w-full">
            <UICard className="h-full w-full md:min-h-[480px]">
              <div className="mb-10 flex items-start justify-between gap-4">
                <div className="flex min-w-0 flex-col gap-4">
                  <CurrencySwitcher
                    currency={currency}
                    activatedCurrencies={activatedCurrencies}
                    availableCurrencies={availableCurrencies}
                    hiddenCurrencies={hiddenCurrencies}
                    walletSort={userSettings?.wallet_sort ?? 'default'}
                    wallets={userWallets ?? []}
                    onCurrencySelect={handleCurrencyChange}
                    isRequestingWallet={isRequestingWallet}
                    walletsLoading={walletsLoading}
                  />
                </div>
                {shouldShowBankDetails && (
                  <BankDetailsDialog
                    systemBankDetails={systemBankDetails as SystemBankDetails}
                    referenceNumber={walletBalance?.reference_number}
                    isLoading={systemBankDetailsLoading || walletBalanceLoading}
                    error={systemBankDetailsError || walletBalanceError}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 shrink-0 rounded-[60px] px-0 text-xs font-medium text-neutral-200 hover:bg-transparent dark:text-neutral-100"
                    >
                      Bank details
                    </Button>
                  </BankDetailsDialog>
                )}
              </div>

              <div className="space-y-5">
                <div className="flex flex-col">
                  <div className="mb-1 flex items-center gap-2 text-base text-grey-500 dark:text-grey-400">
                    <span>{'Available balance'}</span>
                    <InfoTooltip>
                      <InfoTooltip.Content>
                        {
                          'The amount you can immediately use for transactions, transfers, and withdrawals. This excludes any reserved or pending amounts.'
                        }
                      </InfoTooltip.Content>
                    </InfoTooltip>
                  </div>
                  <div className="text-foreground text-[40px] leading-[52px] font-medium dark:text-neutral-100">
                    {walletBalance ? formatAmount(walletBalance.available_balance) : '0.00'}{' '}
                    {currency.code}
                  </div>
                </div>

                <div className="mb-10 space-y-5">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2 text-sm text-grey-500 dark:text-grey-400">
                      <span>{'Current balance'}</span>
                      <InfoTooltip>
                        <InfoTooltip.Content>
                          {
                            'Your total wallet balance including all funds, both available and reserved. This is the sum of available and reserved balances.'
                          }
                        </InfoTooltip.Content>
                      </InfoTooltip>
                    </div>
                    <span className="text--foreground text-base font-medium dark:text-neutral-100">
                      {walletBalance ? formatAmount(walletBalance.current_balance) : '0.00'}{' '}
                      {currency.code}
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <div className="flex items-center gap-2 text-sm text-grey-500 dark:text-grey-400">
                      <p className="m-0">{'Reserved balance'}</p>
                      <InfoTooltip>
                        <InfoTooltip.Content>
                          {
                            'Funds temporarily held for pending transactions, scheduled payments, or security purposes. These funds are not available for immediate use until the holds are released.'
                          }
                        </InfoTooltip.Content>
                      </InfoTooltip>
                    </div>
                    <span className="text--foreground text-base font-medium dark:text-neutral-100">
                      {walletBalance ? formatAmount(walletBalance.reserved_balance) : '0.00'}{' '}
                      {currency.code}
                    </span>
                  </div>
                  {walletBalance?.balance_rewards_enabled && (
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2 text-sm text-grey-500 dark:text-grey-400">
                        <p className="m-0">{'Pending balance rewards'}</p>
                        <InfoTooltip>
                          <InfoTooltip.Content>
                            {
                              'This is the amount of rewards accumulated daily based on your wallet balance. The total is added to your available balance on the first day of each month.'
                            }
                          </InfoTooltip.Content>
                        </InfoTooltip>
                      </div>
                      <span className="text--foreground text-base font-medium dark:text-neutral-100">
                        {walletBalance
                          ? formatAmount(walletBalance.pending_balance_rewards_total)
                          : '0.00'}{' '}
                        {currency.code}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 sm:gap-8">
                <div className="flex flex-col items-center">
                  <Link
                    href={`/wallet/add?${currencyCodeUrlParam}`}
                    className="flex flex-col items-center"
                    onClick={guardNavigation}
                  >
                    <Button variant="quick-action" size="base">
                      <PlusIcon className="h-6 w-6" />
                    </Button>
                    <span className="text-grey-600 dark:text-grey-400 mt-2 text-sm">Add</span>
                  </Link>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center">
                      {hasQueuedPayments ? (
                        <div className="flex flex-col items-center opacity-50">
                          <Button variant="quick-action" size="base" disabled>
                            <SendIcon className="h-6 w-6" />
                          </Button>
                          <span className="text-grey-600 dark:text-grey-400 mt-2 text-sm">
                            Send
                          </span>
                        </div>
                      ) : (
                        <Link
                          href={`/wallet/send?${currencyCodeUrlParam}`}
                          className="flex flex-col items-center"
                          onClick={(event) => {
                            if (guardSendMoneyNavigation(event)) return;
                            guardNavigation(event);
                          }}
                        >
                          <Button variant="quick-action" size="base">
                            <SendIcon className="h-6 w-6" />
                          </Button>
                          <span className="text-grey-600 dark:text-grey-400 mt-2 text-sm">
                            Send
                          </span>
                        </Link>
                      )}
                    </div>
                  </TooltipTrigger>
                  {hasQueuedPayments && (
                    <TooltipContent>
                      <p>Complete the queued payment to continue making transfers.</p>
                    </TooltipContent>
                  )}
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={`flex flex-col items-center ${hasQueuedPayments ? 'opacity-50' : 'cursor-pointer'}`}
                      onClick={
                        hasQueuedPayments
                          ? undefined
                          : () => guard(() => setIsCurrencyConversionDialogOpen(true))
                      }
                    >
                      <Button variant="quick-action" size="base" disabled={hasQueuedPayments}>
                        <ArrowLeftRight className="h-6 w-6" />
                      </Button>
                      <span className="text-grey-600 dark:text-grey-400 mt-2 text-sm">Convert</span>
                    </div>
                  </TooltipTrigger>
                  {hasQueuedPayments && (
                    <TooltipContent>
                      <p>Complete the queued payment to continue making transfers.</p>
                    </TooltipContent>
                  )}
                </Tooltip>
                {isBatchTransfersEnabled && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col items-center">
                        {hasQueuedPayments ? (
                          <div className="flex flex-col items-center opacity-50">
                            <Button variant="quick-action" size="base" disabled>
                              <Coins className="h-6 w-6" />
                            </Button>
                            <span className="text-grey-600 dark:text-grey-400 mt-2 text-center text-sm">
                              Batch <br /> transfers
                            </span>
                          </div>
                        ) : (
                          <Link
                            href="/wallet/batch-transfer"
                            className="flex flex-col items-center"
                            onClick={guardNavigation}
                          >
                            <Button variant="quick-action" size="base">
                              <Coins className="h-6 w-6" />
                            </Button>
                            <span className="text-grey-600 dark:text-grey-400 mt-2 text-center text-sm">
                              Batch <br /> transfers
                            </span>
                          </Link>
                        )}
                      </div>
                    </TooltipTrigger>
                    {hasQueuedPayments && (
                      <TooltipContent>
                        <p>Complete the queued payment to continue making transfers.</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                )}
              </div>
            </UICard>
          </div>

          {/* Card Display - Show Wallet Card if display card exists, otherwise show Card Request Status (ordering banner) */}
          {/* User's card: show when not forbidden country and currency supported. */}
          {shouldShowCardComponents &&
            (isCardStatusLoading ? (
              <CardDisplaySkeleton />
            ) : displayCard ? (
              <WalletCard card={displayCard} />
            ) : (
              !isFromForbiddenCountry && (
                <CardRequestStatus
                  status={cardRequestStatus}
                  cardData={cardData}
                  onActivationSuccess={() => setShowCardActivationSuccess(true)}
                />
              )
            ))}
        </div>

        {/* {showMobileAppPromoBanner ? <MobileAppPromoBanner /> : null} */}

        {/* Queued Payments Section */}
        <QueuedPaymentsSection
          currencyCode={currency.code}
          enabled={isBatchTransfersEnabled && isAuthenticated && !!accessToken}
          onViewDetails={handleViewDetails}
          onQueuedPaymentsChange={setQueuedCurrencyCodes}
        />

        {/* Latest Transactions Section */}
        <TransactionsTable currencies={activatedCurrencies} onViewDetails={handleViewDetails} />

        {/* Transaction Details Dialog */}
        <TransactionDetailsDialog
          transaction={selectedTransaction}
          open={isDialogOpen}
          onOpenChange={handleDialogClose}
          expenseCategories={expenseCategories}
        />

        {/* Currency conversion dialog */}
        <CurrencyConversionDialog
          open={isCurrencyConversionDialogOpen}
          onOpenChange={setIsCurrencyConversionDialogOpen}
        />

        {/* Salary wallet activation announcement (one-time, eligible users only) */}
        <SalaryWalletActiveDialog
          userId={user?.user?.id}
          canAccessSalaryWallet={hasSalaryWalletAccess}
        />

        {/* Card activation success dialog — kept at page level so it stays mounted
            after the card status transitions from the request banner to the active card. */}
        <CardActivationSuccessDialog
          open={showCardActivationSuccess}
          onOpenChange={setShowCardActivationSuccess}
        />

        <RestrictionDialog {...restrictionDialogProps} />
        <FeatureLockedDialog {...sendMoneyFeatureLockedDialogProps} />
      </div>
    </div>
  );
}

function CardDisplaySkeleton() {
  return (
    <div className="flex w-full md:max-w-[480px]">
      <UICard className="h-full w-full overflow-hidden border-none p-6 md:min-h-[480px] md:w-[474px] md:max-w-[474px]">
        <div className="mb-6 flex items-center justify-between">
          <Skeleton className="bg-grey-200 dark:bg-grey-800 h-6 w-32" />
          <Skeleton className="bg-grey-200 dark:bg-grey-800 h-8 w-24" />
        </div>
        <div className="mb-8 flex items-center gap-2">
          <Skeleton className="bg-grey-200 dark:bg-grey-800 h-5 w-5 rounded-full" />
          <Skeleton className="bg-grey-200 dark:bg-grey-800 h-5 w-16" />
        </div>
        <div className="relative mx-auto w-full">
          <Skeleton className="bg-grey-200 dark:bg-grey-800 aspect-[1.6/1] w-full max-w-[400px] rounded-2xl" />
        </div>
      </UICard>
    </div>
  );
}
